import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Lock, Eye, EyeOff, KeyRound } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';

const FIELDS = [
  { key: 'currentPassword', label: 'Current Password', testid: 'current-password-input' },
  { key: 'newPassword', label: 'New Password', testid: 'new-password-input' },
  { key: 'confirmPassword', label: 'Confirm New Password', testid: 'confirm-password-input' },
];

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [visible, setVisible] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (key, v) => setValues((p) => ({ ...p, [key]: v }));
  const toggleVisible = (key) => setVisible((p) => ({ ...p, [key]: !p[key] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (values.newPassword.length < 6) {
      toast({ title: 'Password too short', description: 'New password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    if (values.newPassword !== values.confirmPassword) {
      toast({ title: "Passwords don't match", description: 'New password and confirmation must be the same', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.put('/api/auth/driver/password', {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      });
      toast({ title: 'Password Updated', description: 'Use your new password the next time you log in' });
      setValues({ currentPassword: '', newPassword: '', confirmPassword: '' });
      navigate('/driver/dashboard');
    } catch (err) {
      toast({ title: 'Could not change password', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/driver/dashboard')} data-testid="change-password-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <BrandLogo variant="compact" className="h-8 w-auto" />
          <h1 className="text-xl font-bold border-l pl-4">Change Password</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-md">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              Set a new password for your account. You will keep using the same Driver ID to log in.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {FIELDS.map(({ key, label, testid }) => (
              <div key={key}>
                <Label htmlFor={key}>{label}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-muted-foreground/70" />
                  <Input
                    id={key}
                    type={visible[key] ? 'text' : 'password'}
                    value={values[key]}
                    onChange={(e) => setField(key, e.target.value)}
                    className="pl-10 pr-10 h-12"
                    data-testid={testid}
                  />
                  <button
                    type="button"
                    onClick={() => toggleVisible(key)}
                    className="absolute right-3 top-3 text-muted-foreground/70 hover:text-foreground"
                    data-testid={`toggle-${testid}`}
                    aria-label={visible[key] ? 'Hide password' : 'Show password'}
                  >
                    {visible[key] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            ))}
            <Button type="submit" className="w-full bg-primary hover:bg-primary-dark h-12" disabled={submitting}
              data-testid="change-password-submit-btn">
              {submitting ? 'Updating…' : 'Update Password'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
